import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useAthlete } from '@/context/AthleteContext';
import { Home, Play, Clock, MapPin, Dumbbell, ChevronRight, Sparkles, AlertCircle, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FOCUS_AREAS = [
  { key: 'ball_control', label: 'Ball Control', icon: '⚽' },
  { key: 'passing', label: 'Passing', icon: '🎯' },
  { key: 'dribbling', label: 'Dribbling', icon: '💨' },
  { key: 'first_touch', label: 'First Touch', icon: '👆' },
  { key: 'speed_agility', label: 'Speed & Agility', icon: '⚡' },
  { key: 'coordination', label: 'Coordination', icon: '🤸' },
];

const TIME_OPTIONS = [
  { value: 10, label: '10 min' },
  { value: 15, label: '15 min' },
  { value: 20, label: '20 min' },
  { value: 30, label: '30 min' },
];

const SPACE_OPTIONS = [
  { value: 'small', label: 'Small Space', description: 'Indoor or tight area' },
  { value: 'backyard', label: 'Backyard', description: 'Medium outdoor space' },
  { value: 'field', label: 'Field', description: 'Full outdoor space' },
];

const TrainAtHome = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const { athlete } = useAthlete();
  const [step, setStep] = useState(1);
  const [selectedFocus, setSelectedFocus] = useState([]);
  const [selectedTime, setSelectedTime] = useState(15);
  const [selectedSpace, setSelectedSpace] = useState('backyard');
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [homeDrills, setHomeDrills] = useState([]);
  const [checkingDrills, setCheckingDrills] = useState(true);
  const [addToCalendar, setAddToCalendar] = useState(true);

  useEffect(() => {
    checkHomeDrills();
  }, []);

  const checkHomeDrills = async () => {
    setCheckingDrills(true);
    try {
      const res = await axios.get(`${API}/drills/home`, { headers: getAuthHeaders() });
      setHomeDrills(res.data);
    } catch (error) {
      console.error('Failed to fetch home drills:', error);
    }
    setCheckingDrills(false);
  };

  const handleToggleFocus = (key) => {
    setSelectedFocus(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  const handleGenerateSession = async () => {
    if (selectedFocus.length === 0) {
      toast.error('Please select at least one focus area');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/generate-home-training`, {
        focus_areas: selectedFocus,
        available_time: selectedTime,
        space: selectedSpace
      }, { headers: getAuthHeaders() });
      
      setSession(res.data);
      setStep(4);
    } catch (error) {
      toast.error('Failed to generate session');
    }
    setLoading(false);
  };

  const resetSession = () => {
    setStep(1);
    setSelectedFocus([]);
    setSelectedTime(15);
    setSelectedSpace('backyard');
    setSession(null);
  };

  const handleStartTraining = async () => {
    // Create calendar event if toggle is on
    if (addToCalendar && session && user) {
      try {
        const now = new Date();
        const endTime = new Date(now.getTime() + selectedTime * 60 * 1000);
        
        await axios.post(`${API}/calendar/events?user_id=${user.id}`, {
          title: `Home Training: ${session.session_title}`,
          event_type: 'home_training',
          start_time: now.toISOString(),
          end_time: endTime.toISOString(),
          athlete_id: athlete?.id,
          athlete_name: athlete?.name,
          notes: `${session.drills.length} drills - ${session.total_duration} min`,
          status: 'confirmed'
        }, { headers: getAuthHeaders() });
        
        toast.success('Training added to calendar');
      } catch (error) {
        console.error('Failed to add to calendar:', error);
      }
    }
    
    navigate('/dashboard/train-at-home/guided', { state: { session } });
  };

  // Empty state - no home drills available
  if (!checkingDrills && homeDrills.length === 0) {
    return (
      <div className="space-y-6" data-testid="train-at-home-empty">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Train at Home</h1>
          <p className="text-muted-foreground">Personal training sessions for {athlete?.name}</p>
        </div>

        <div className="bg-card border border-border/40 rounded-sm p-8 text-center">
          <AlertCircle className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="font-heading text-xl mb-2">No Home Training Drills Available</h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            Your coach has not added Home Training drills yet. 
            Check back soon or ask your coach to enable this feature.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="train-at-home">
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight">Train at Home</h1>
        <p className="text-muted-foreground">Personal training sessions for {athlete?.name}</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3, 4].map(s => (
          <div 
            key={s}
            className={`flex-1 h-1 rounded-full transition-colors ${
              s <= step ? 'bg-primary' : 'bg-muted'
            }`}
          />
        ))}
      </div>

      {/* Step 1: Select Focus Areas */}
      {step === 1 && (
        <div className="bg-card border border-border/40 rounded-sm p-6">
          <h2 className="font-heading text-xl uppercase mb-4">What do you want to work on?</h2>
          <p className="text-sm text-muted-foreground mb-4">Select one or more focus areas</p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            {FOCUS_AREAS.map(area => (
              <button
                key={area.key}
                onClick={() => handleToggleFocus(area.key)}
                className={`p-4 rounded-sm border text-left transition-colors ${
                  selectedFocus.includes(area.key)
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-muted/20 border-border hover:border-primary/50'
                }`}
              >
                <span className="text-2xl mb-2 block">{area.icon}</span>
                <span className="font-medium text-sm">{area.label}</span>
              </button>
            ))}
          </div>

          <Button 
            onClick={() => setStep(2)} 
            disabled={selectedFocus.length === 0}
            className="w-full bg-primary text-black"
          >
            Next: Select Time
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Step 2: Select Time */}
      {step === 2 && (
        <div className="bg-card border border-border/40 rounded-sm p-6">
          <h2 className="font-heading text-xl uppercase mb-4">How much time do you have?</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {TIME_OPTIONS.map(option => (
              <button
                key={option.value}
                onClick={() => setSelectedTime(option.value)}
                className={`p-4 rounded-sm border text-center transition-colors ${
                  selectedTime === option.value
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-muted/20 border-border hover:border-primary/50'
                }`}
              >
                <Clock className="mx-auto mb-2 h-6 w-6" />
                <span className="font-heading text-lg">{option.label}</span>
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
              Back
            </Button>
            <Button onClick={() => setStep(3)} className="flex-1 bg-primary text-black">
              Next: Select Space
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Select Space */}
      {step === 3 && (
        <div className="bg-card border border-border/40 rounded-sm p-6">
          <h2 className="font-heading text-xl uppercase mb-4">Where will you train?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            {SPACE_OPTIONS.map(option => (
              <button
                key={option.value}
                onClick={() => setSelectedSpace(option.value)}
                className={`p-4 rounded-sm border text-left transition-colors ${
                  selectedSpace === option.value
                    ? 'bg-primary/10 border-primary'
                    : 'bg-muted/20 border-border hover:border-primary/50'
                }`}
              >
                <MapPin className={`mb-2 h-6 w-6 ${selectedSpace === option.value ? 'text-primary' : ''}`} />
                <span className="font-medium block">{option.label}</span>
                <span className="text-xs text-muted-foreground">{option.description}</span>
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
              Back
            </Button>
            <Button onClick={handleGenerateSession} disabled={loading} className="flex-1 bg-primary text-black">
              {loading ? (
                <>Generating...</>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Session
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Session Result */}
      {step === 4 && session && (
        <div className="space-y-4">
          {session.drills.length === 0 ? (
            <div className="bg-card border border-border/40 rounded-sm p-8 text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h2 className="font-heading text-xl mb-2">No Matching Drills Found</h2>
              <p className="text-muted-foreground mb-4">
                Try selecting different focus areas or space options.
              </p>
              <Button onClick={resetSession}>Try Again</Button>
            </div>
          ) : (
            <>
              <div className="bg-card border border-primary/30 rounded-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-heading text-xl uppercase">{session.session_title}</h2>
                    <p className="text-sm text-muted-foreground">
                      {session.drills.length} drills • {session.total_duration} min total
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-primary uppercase tracking-wider">
                      Generated from your Coach's Methodology
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  {session.drills.map((drill, index) => (
                    <div 
                      key={drill.id}
                      className="bg-muted/20 border border-border/40 rounded-sm p-4"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-heading text-primary">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium">{drill.name}</h3>
                          <p className="text-sm text-muted-foreground mt-1">{drill.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {drill.home_duration_minutes} min
                            </span>
                            {drill.equipment_required !== 'none' && (
                              <span className="flex items-center gap-1">
                                <Dumbbell size={12} />
                                {drill.equipment_required}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={resetSession} className="flex-1">
                  Generate New Session
                </Button>
                <Button 
                  className="flex-1 bg-primary text-black"
                  onClick={handleStartTraining}
                >
                  <Play className="mr-2 h-4 w-4" />
                  Start Training
                </Button>
              </div>
              
              {/* Add to Calendar Toggle */}
              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/40">
                <div className="flex items-center gap-2">
                  <Calendar size={18} className="text-primary" />
                  <span className="text-sm">Add to Calendar</span>
                </div>
                <button
                  onClick={() => setAddToCalendar(!addToCalendar)}
                  className={`w-11 h-6 rounded-full transition-colors ${
                    addToCalendar ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                    addToCalendar ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TrainAtHome;
